import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Heading, Paragraph, YStack } from 'tamagui';

interface Props {
  children: ReactNode;
  onReset?: () => void;
  onError?: (error: Error, info: ErrorInfo) => void;
  fallbackRender?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

function DefaultFallback({ error, onReset }: { error: Error | null; onReset: () => void }) {
  return (
    <YStack flex={1} alignItems="center" justifyContent="center" padding="$6" gap="$4">
      <Heading size="$6">Etwas ist schiefgelaufen</Heading>
      {error?.message ? (
        <Paragraph size="$3" color="$red10" textAlign="center">
          {error.message}
        </Paragraph>
      ) : null}
      <Button onPress={onReset}>Erneut versuchen</Button>
    </YStack>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  override render() {
    if (!this.state.hasError) return this.props.children;

    const { error } = this.state;
    const { fallbackRender } = this.props;

    if (fallbackRender) return fallbackRender(error!, this.handleReset);

    return <DefaultFallback error={error} onReset={this.handleReset} />;
  }
}
